import { scaleLinear, scaleTime } from 'd3-scale';
import { formatSeriesData } from '../Charts/MultiPredictionTimeSeriesChart/utils';

function getStackedChartFns({ series, xRange0, xRange1, yRange0, yRange1 }) {
    const { accessors, xMin, xMax, formattedData } = formatSeriesData(series);
    const { keys, predictionKeys } = series.reduce(
        (acc, { id }) => {
            const accKey = id === 'overview' ? 'predictionKeys' : 'keys';
            acc[accKey].push(id);
            return acc;
        },
        { keys: [], predictionKeys: [] },
    );

    const backfillIdxById = series.reduce((acc, s) => {
        acc[s.id] = Array.isArray(s.timeSeries)
            ? s.timeSeries[0]?.backfill
            : undefined;
        return acc;
    }, {});

    const stackData = Object.values(
        formattedData.reduce((acc, series) => {
            if (series.id === 'overview') return acc;
            const backfillIdx = backfillIdxById[series.id];
            return series.historical.reduce((acc, row) => {
                const key = accessors.xAccessor(row);
                if (!acc[key]) acc[key] = { date: key };
                acc[key][series.id] = accessors.yAccessor(row);
                if (backfillIdx != null)
                    acc[key][`${series.id}_backfill`] = !!row[backfillIdx];
                return acc;
            }, acc);
        }, {}),
    )
        .map((row) => {
            if (keys.some((key) => !row[key])) {
                keys.forEach((key) => {
                    if (!row[key]) row[key] = null;
                });
            }
            return row;
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date));

    const predictionStackData = Object.values(
        formattedData.reduce((acc, series) => {
            if (series.id !== 'overview') return acc;
            return series.historical.reduce((acc, row) => {
                const key = accessors.xAccessor(row);
                if (!acc[key]) acc[key] = { date: key };
                acc[key][series.id] = accessors.yAccessor(row);
                return acc;
            }, acc);
        }, {}),
    ).sort((a, b) => new Date(a.date) - new Date(b.date));

    // sort keys based on which key has highest total volume
    keys.sort((a, b) => {
        // if key is 'overview' then always sort last
        if (a === 'overview') return 1;
        if (b === 'overview') return -1;
        const aTotal = stackData.reduce((acc, row) => acc + (row[a] ?? 0), 0);
        const bTotal = stackData.reduce((acc, row) => acc + (row[b] ?? 0), 0);
        return aTotal - bTotal;
    });

    // stackData and predictionStackData combined for determining full yMin, yMax, and xMax, xMin
    const fullStackData = stackData.concat(predictionStackData);
    const fullKeys = Array.from(new Set(keys.concat(predictionKeys)));

    const yMin = 0;
    const yMax = Math.max(
        ...fullStackData.map((row) =>
            fullKeys.reduce((acc, key) => acc + (row[key] ?? 0), 0),
        ),
    );
    const xScale = scaleTime().domain([xMin, xMax]).range([xRange0, xRange1]);
    const yScale = scaleLinear().domain([yMin, yMax]).range([yRange0, yRange1]);
    const x = (d) => xScale(new Date(d.data.date));
    const y0 = (d) => yScale(d[0]);
    const y1 = (d) => yScale(d[1]);

    return {
        x,
        y0,
        y1,
        stackData,
        predictionStackData,
        predictionKeys,
        keys,
        xScale,
        yScale,
        xMin,
        xMax,
        yMin,
        yMax,
        accessors,
        formattedData,
    };
}

export default getStackedChartFns;
